import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Kinetic";
import { api, startCheckout } from "@/lib/api";

const CIRCLE_PERKS = [
  "Unreleased tracks before anyone else",
  "Early access to every merch drop",
  "Members-only freestyles & behind-the-scenes",
  "Discount codes on the whole shop",
];

export default function Subscribe() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [buying, setBuying] = useState(null);

  const join = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", { email, name: name || undefined });
      setJoined(true);
      toast.success("You're on the list. Welcome to the Smoke.");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("You're already on the list.");
        setJoined(true);
      } else {
        toast.error("Something went wrong — try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const joinCircle = async (lookupKey) => {
    setBuying(lookupKey);
    try {
      await startCheckout(lookupKey);
    } catch {
      toast.error("Checkout failed — try again.");
      setBuying(null);
    }
  };

  return (
    <main data-testid="subscribe-page" className="mx-auto max-w-7xl px-6 pb-40 pt-36">
      <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">STAY PLUGGED IN</p>
      <h1 data-testid="subscribe-title" className="font-anton leading-[0.9]">
        <MaskedLine className="text-6xl text-white md:text-8xl" delay={0.1}>JOIN THE</MaskedLine>
        <MaskedLine className="text-outline glow-blue text-6xl md:text-8xl" delay={0.25}>SMOKE</MaskedLine>
      </h1>

      <div className="mt-24 grid gap-16 lg:grid-cols-2">
        <Reveal>
          <div data-testid="newsletter-section" className="border border-white/10 bg-card p-10 md:p-14">
            <p className="font-mono text-xs tracking-[0.4em] text-primary">FREE — THE FAN LIST</p>
            <h2 className="mt-4 font-anton text-4xl text-white md:text-5xl">SMOKE SIGNALS</h2>
            <p className="mt-4 text-white/60">
              One email when it matters: new music, tour dates, drop alerts. No spam, ever.
            </p>

            {joined ? (
              <div data-testid="newsletter-success" className="mt-10 flex items-center gap-4 border border-primary/40 bg-primary/10 p-6">
                <span className="bg-primary p-2 text-black"><Check size={18} /></span>
                <p className="font-mono text-xs tracking-[0.2em] text-primary">YOU'RE IN. WATCH YOUR INBOX.</p>
              </div>
            ) : (
              <form data-testid="newsletter-form" onSubmit={join} className="mt-10 space-y-8">
                <div>
                  <label htmlFor="sub-name" className="font-mono text-[10px] tracking-[0.3em] text-white/50">NAME (OPTIONAL)</label>
                  <input
                    id="sub-name"
                    data-testid="newsletter-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-lg text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-primary"
                  />
                </div>
                <div>
                  <label htmlFor="sub-email" className="font-mono text-[10px] tracking-[0.3em] text-white/50">EMAIL</label>
                  <input
                    id="sub-email"
                    data-testid="newsletter-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-lg text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-primary"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="newsletter-submit-button"
                  disabled={loading}
                  className="flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#fff] disabled:opacity-50"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? "JOINING..." : "JOIN FREE"}
                </button>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="circle-section" className="relative border border-primary/40 bg-black p-10 md:p-14">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/20 blur-[90px]" />
            <p className="font-mono text-xs tracking-[0.4em] text-primary">PAID — MEMBERSHIP</p>
            <h2 className="mt-4 font-anton text-4xl text-white md:text-5xl">
              MILLZZ <span className="text-primary glow-blue">CIRCLE</span>
            </h2>
            <ul className="mt-8 space-y-4">
              {CIRCLE_PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-white/70">
                  <Check size={16} className="mt-1 shrink-0 text-primary" />
                  {perk}
                </li>
              ))}
            </ul>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <button
                data-testid="circle-monthly-button"
                onClick={() => joinCircle("circle_monthly")}
                disabled={!!buying}
                className="border border-white/25 p-6 text-left transition-colors duration-300 hover:border-primary disabled:opacity-50"
              >
                <p className="font-mono text-[10px] tracking-[0.3em] text-white/50">MONTHLY</p>
                <p className="mt-2 font-anton text-4xl text-white">$4.99</p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-white/40">PER MONTH — CANCEL ANYTIME</p>
              </button>
              <button
                data-testid="circle-yearly-button"
                onClick={() => joinCircle("circle_yearly")}
                disabled={!!buying}
                className="border border-primary bg-primary/10 p-6 text-left transition-transform duration-300 hover:-translate-y-1 disabled:opacity-50"
              >
                <p className="font-mono text-[10px] tracking-[0.3em] text-primary">YEARLY — SAVE 17%</p>
                <p className="mt-2 font-anton text-4xl text-white">$49.99</p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-white/40">PER YEAR — 2 MONTHS FREE</p>
              </button>
            </div>
            <p data-testid="circle-secure-note" className="mt-6 font-mono text-[10px] tracking-[0.25em] text-white/30">
              {buying ? "REDIRECTING TO SECURE CHECKOUT..." : "SECURE CHECKOUT POWERED BY STRIPE"}
            </p>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
